/**
 * /notices — Zentrale Mitteilungen (plan 2026-09-23-zentrale-mitteilungen, phase M2).
 *
 * One page to post an operating notice to one or more Dolder apps (Pure Brain, Customer 360,
 * Reporting App, Supplier Order Management — the registry `[Reporting].[NoticeApp]`). The apps
 * fetch it backend to backend from `/api/v1/notices/active`; this page never talks to them.
 *
 * Posting and ending need CanEdit on /notices (D6: Roman only at first, widened on the
 * permission pages). Viewing the history needs CanView. The full history — running, scheduled,
 * ended early and expired — lives here and nowhere else (D5).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Megaphone, RotateCw } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useToast } from '../components/ui/Toast';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { NoticeForm } from '../components/notices/NoticeForm';
import { NoticeTable } from '../components/notices/NoticeTable';
import { noticesApi } from '../services/noticesApi';
import type { NewNoticeRequest, NoticeApp, NoticeRecord, NoticeTargetPage } from '../types/notices';

export function NoticesPage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/notices');
    const { toast } = useToast();

    const [notices, setNotices] = useState<NoticeRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [limit, setLimit] = useState(0);
    const [apps, setApps] = useState<NoticeApp[]>([]);
    const [pages, setPages] = useState<NoticeTargetPage[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmEnd, setConfirmEnd] = useState<NoticeRecord | null>(null);
    const [endingId, setEndingId] = useState<number | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // The page list only feeds the optional page target; if it fails, the form still
            // posts app-wide notices rather than the whole page refusing to load.
            const [list, registry, targetPages] = await Promise.all([
                noticesApi.list(), noticesApi.apps(), noticesApi.pages().catch(() => []),
            ]);
            setNotices(list.notices);
            setTotal(list.total);
            setLimit(list.limit);
            setApps(registry);
            setPages(targetPages);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load notices');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canView) void load();
    }, [canView, load]);

    const current = useMemo(() => notices.filter(n => n.status === 'active' || n.status === 'scheduled'), [notices]);
    const past = useMemo(() => notices.filter(n => n.status === 'ended' || n.status === 'expired'), [notices]);

    const create = useCallback(async (req: NewNoticeRequest): Promise<boolean> => {
        setSubmitting(true);
        try {
            const stored = await noticesApi.create(req);
            toast(`Notice #${stored.id} posted to ${stored.apps.length} app${stored.apps.length === 1 ? '' : 's'}`, 'success');
            await load();
            return true;
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to post the notice', 'error');
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [toast, load]);

    const end = useCallback(async (n: NoticeRecord) => {
        setConfirmEnd(null);
        setEndingId(n.id);
        try {
            await noticesApi.expire(n.id);
            toast(`Notice #${n.id} ended`, 'success');
            await load();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to end the notice', 'error');
            await load();
        } finally {
            setEndingId(null);
        }
    }, [toast, load]);

    if (permLoading) {
        return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>;
    }
    if (!canView) {
        return <div className="flex h-64 items-center justify-center"><p className="text-gray-500">You do not have access to this page.</p></div>;
    }

    return (
        <div className="space-y-6 p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-4">
                <div className="flex items-baseline gap-4">
                    <h1 className="text-2xl font-bold text-gray-900">Notices</h1>
                    <p className="text-xs italic text-gray-500">One operating notice, shown as a band at the top of every app you tick</p>
                </div>
                <button
                    type="button"
                    onClick={() => void load()}
                    disabled={loading}
                    className="inline-flex items-center gap-1 rounded border px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                    <RotateCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
                </button>
            </div>

            {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

            {canEdit && (
                <section className="rounded-lg border bg-white p-5 shadow-sm">
                    <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-800">
                        <Megaphone className="h-4 w-4 text-gray-500" /> New notice
                    </h2>
                    <NoticeForm apps={apps} pages={pages} submitting={submitting} onSubmit={create} />
                </section>
            )}

            <section className="overflow-hidden rounded-lg border bg-white shadow-sm">
                <h2 className="border-b px-4 py-3 text-sm font-semibold text-gray-800">
                    Running and scheduled <span className="font-normal text-gray-500">({current.length})</span>
                </h2>
                {loading && notices.length === 0
                    ? <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div>
                    : <NoticeTable
                        rows={current}
                        apps={apps}
                        storageKey="notices-current-col-widths"
                        emptyText="No notice is running or scheduled."
                        canEdit={canEdit}
                        endingId={endingId}
                        onEnd={setConfirmEnd}
                        initialSort={{ key: 'validFrom', dir: 'desc' }}
                        maxHeightClass="max-h-[40vh]"
                    />}
            </section>

            <section className="overflow-hidden rounded-lg border bg-white shadow-sm">
                <h2 className="border-b px-4 py-3 text-sm font-semibold text-gray-800">
                    Past <span className="font-normal text-gray-500">({past.length})</span>
                    {total > limit && <span className="ml-2 text-xs font-normal text-amber-700">Showing the newest {limit} of {total}.</span>}
                </h2>
                {!(loading && notices.length === 0) && (
                    <NoticeTable
                        rows={past}
                        apps={apps}
                        storageKey="notices-past-col-widths"
                        emptyText="No past notices yet."
                        canEdit={false}
                        endingId={null}
                        initialSort={{ key: 'validUntil', dir: 'desc' }}
                        maxHeightClass="max-h-[60vh]"
                    />
                )}
            </section>

            {confirmEnd && (
                <ConfirmModal
                    title={`End notice #${confirmEnd.id}?`}
                    message={`"${confirmEnd.title}" disappears from ${confirmEnd.apps.length === 1 ? 'its target app' : `all ${confirmEnd.apps.length} target apps`} at the next refresh. The notice stays in this list as ended.`}
                    confirmLabel="End notice"
                    onConfirm={() => void end(confirmEnd)}
                    onCancel={() => setConfirmEnd(null)}
                />
            )}
        </div>
    );
}
